<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { ROLES } from "@/services/acl";
import {
  fetchUsersAnalysisChart,
  fetchPlansAnalysisReport,
  fetchComplaintsAnalysisChart,
  fetchMessagesAnalysisChart,
  fetchRelationsAnalysisChart,
  fetchPlans,
  fetchUtopiaBalance,
} from "@/services/backofficeClient";
import UserFilters from "@/views/analysis/components/UserFilters.vue";
import PlanFilters from "@/views/analysis/components/PlanFilters.vue";
import DateGranularityFilter from "@/views/analysis/components/DateGranularityFilter.vue";
import DimensionWidget from "@/views/analysis/components/DimensionWidget.vue";
import {
  TABS,
  Y_DIMENSION_OPTIONS,
  PURCHASE_DIMENSION_OPTIONS,
  WALLET_DIMENSION_OPTIONS,
  COMPLAINT_DIMENSION_OPTIONS,
  MESSAGE_DIMENSION_OPTIONS,
  RELATION_DIMENSION_OPTIONS,
  AGE_MIN,
  AGE_MAX,
  DEFAULT_TIMEZONE,
} from "./analysisOptions";
import { toDateInputValue, toDateRangeParams, formatTimestamp } from "./analysisFormat";
import { pivotByDateAndBreakdown, capSeriesWithOthers } from "./analysisCharts";
import { createUrlQuery } from "./urlQuery";

const defaultDateTo = new Date();
const defaultDateFrom = new Date();
defaultDateFrom.setDate(defaultDateFrom.getDate() - 6);
const DEFAULT_DATE_FROM_STR = toDateInputValue(defaultDateFrom);
const DEFAULT_DATE_TO_STR = toDateInputValue(defaultDateTo);

// --- URL persistence ---------------------------------------------------------
//
// Selected tab and filters are mirrored to the URL query string so a refresh
// lands back on the same view. User-tab and plan-tab filters are kept under
// distinct prefixed keys ("u"/"p") since several field names (granularity,
// minAge, statuses, ...) exist in both groups.

const route = useRoute();
const router = useRouter();
const store = useStore();
const { queryStr, queryList, queryNum } = createUrlQuery(route);

const activeTabKey = ref(
  TABS.some((t) => t.key === route.query.tab) ? route.query.tab : TABS[0].key
);
const activeTab = computed(() => TABS.find((t) => t.key === activeTabKey.value));
const isUserTab = computed(() => activeTab.value.group === "user");
const isWalletTab = computed(() => activeTab.value.key === "wallet");
const isPurchasesTab = computed(() => activeTab.value.key === "purchases");
const isComplaintTab = computed(() => activeTab.value.group === "complaint");
const isMessageTab = computed(() => activeTab.value.group === "message");
const isRelationTab = computed(() => activeTab.value.group === "relation");
const canViewUtopiaBalance = computed(() => store.getters.hasRole(ROLES.PAY_INDEX));

const isLoading = ref(false);

// --- Utopia balance ----------------------------------------------------------
//
// Kept as a manual "Check balance" button rather than auto-fetched with the
// other widgets, since it's a live call to the external Utopia merchant API,
// not a cached/aggregated warehouse query.

const utopiaBalance = ref(null);
const utopiaBalanceCheckedAt = ref(null);
const utopiaBalanceLoading = ref(false);
const utopiaBalanceError = ref("");

const checkUtopiaBalance = async () => {
  if (utopiaBalanceLoading.value) return;
  utopiaBalanceLoading.value = true;
  utopiaBalanceError.value = "";
  try {
    const payload = await fetchUtopiaBalance();
    utopiaBalance.value = Number(payload?.balance ?? 0);
    utopiaBalanceCheckedAt.value = new Date();
  } catch (error) {
    console.error("Failed to fetch Utopia balance", error);
    utopiaBalanceError.value =
      error?.payload?.message ||
      error?.message ||
      "Unable to fetch Utopia balance. Please try again.";
    utopiaBalance.value = null;
  } finally {
    utopiaBalanceLoading.value = false;
  }
};

// --- Users tab ---------------------------------------------------------------

const userFilters = reactive({
  dateField: queryStr("uDateField", "createdAt"),
  granularity: queryStr("uGranularity", "day"),
  timezone: queryStr("uTimezone", DEFAULT_TIMEZONE),
  dateFrom: queryStr("uDateFrom", DEFAULT_DATE_FROM_STR),
  dateTo: queryStr("uDateTo", DEFAULT_DATE_TO_STR),
  countryCodes: queryList("uCountryCodes"),
  platform: queryStr("uPlatform", ""),
  minAge: queryNum("uMinAge", AGE_MIN),
  maxAge: queryNum("uMaxAge", AGE_MAX),
  statuses: queryList("uStatuses"),
  genders: queryList("uGenders"),
  ageGroups: queryList("uAgeGroups"),
  activePlanIds: queryList("uActivePlanIds"),
});

const usersLoadedOnce = ref(false);
const userWidgets = reactive(
  Object.fromEntries(
    Y_DIMENSION_OPTIONS.map((opt) => [
      opt.value,
      { chart: { categories: [], series: [] }, isLoading: false, errorMessage: "" },
    ])
  )
);

// One widget per Y_DIMENSION_OPTIONS entry, fetched in parallel: X = date
// bucket (per the selected date field/granularity), stacked series = that
// widget's dimension values, capped to the top 10 + Others. `x` stays the
// selected date field (not the breakdown dimension) so the date-range
// filter actually applies to the chosen column — GetUsersAnalysisChartQuery
// picks which users column the date range filters on from `x`'s dimension,
// so repurposing x for the breakdown would silently break "Date field".
const loadUsersWidgets = async () => {
  syncUrl();
  isLoading.value = true;
  Y_DIMENSION_OPTIONS.forEach((opt) => {
    userWidgets[opt.value].isLoading = true;
    userWidgets[opt.value].errorMessage = "";
  });
  await Promise.all(
    Y_DIMENSION_OPTIONS.map(async (opt) => {
      const entry = userWidgets[opt.value];
      try {
        const payload = await fetchUsersAnalysisChart({
          x: userFilters.dateField,
          xGranularity: userFilters.granularity,
          y: opt.value,
          yGranularity: userFilters.granularity,
          timezone: userFilters.timezone,
          ...toDateRangeParams(userFilters.dateFrom, userFilters.dateTo, userFilters.timezone),
          status: userFilters.statuses,
          gender: userFilters.genders,
          countryCode: userFilters.countryCodes,
          platform: userFilters.platform,
          activePlanId: userFilters.activePlanIds,
          ageGroup: userFilters.ageGroups,
          // Only send age bounds when the user has actually moved the slider
          // off the full range — otherwise users with no birthdate (e.g.
          // deleted accounts) would be filtered out entirely.
          minAge: userFilters.minAge > AGE_MIN ? userFilters.minAge : undefined,
          maxAge: userFilters.maxAge < AGE_MAX ? userFilters.maxAge : undefined,
        });
        entry.chart = capSeriesWithOthers({
          categories: Array.isArray(payload?.categories) ? payload.categories : [],
          series: Array.isArray(payload?.series) ? payload.series : [],
        });
      } catch (error) {
        console.error(`Failed to load users analysis widget (${opt.value})`, error);
        entry.errorMessage =
          error?.payload?.message || error?.message || "Unable to load this widget.";
        entry.chart = { categories: [], series: [] };
      } finally {
        entry.isLoading = false;
      }
    })
  );
  isLoading.value = false;
  usersLoadedOnce.value = true;
};

// --- Purchases / Wallet / Purchase invoices tabs -----------------------------

const planFilters = reactive({
  granularity: queryStr("pGranularity", "day"),
  timezone: queryStr("pTimezone", DEFAULT_TIMEZONE),
  dateFrom: queryStr("pDateFrom", DEFAULT_DATE_FROM_STR),
  dateTo: queryStr("pDateTo", DEFAULT_DATE_TO_STR),
  addressCountries: queryList("pAddressCountries"),
  userPlanCountries: queryList("pUserPlanCountries"),
  platforms: queryList("pPlatforms"),
  minAge: queryNum("pMinAge", AGE_MIN),
  maxAge: queryNum("pMaxAge", AGE_MAX),
  statuses: queryList("pStatuses"),
  genders: queryList("pGenders"),
  ageGroups: queryList("pAgeGroups"),
  signInBys: queryList("pSignInBys"),
  planIds: queryList("pPlanIds"),
  verificationStatuses: queryList("pVerificationStatuses"),
});

// Which data set the Purchases tab shows.
const purchaseType = ref(queryStr("pPurchaseType", "purchases"));
// Wallet transactions include both top-ups (positive) and spend (negative).
// Spend dwarfs top-ups in volume and skews the breakdown widgets, so it's
// filtered out by default — this opts back in. Unlike the other plan-tab
// filters, this is a real backend query parameter, so changing it refetches
// immediately (see the watcher below) rather than waiting for Apply.
const includeSpent = ref(queryStr("pIncludeSpent", "") === "true");
// All plan-tab widgets show one metric at a time (Purchases/Wallet only —
// Purchase invoices has its own fixed per-widget metric, see below).
const breakdownMetric = ref(queryStr("pBreakdownMetric", "amount"));

const plans = ref([]);
const loadPlans = async () => {
  try {
    const payload = await fetchPlans();
    plans.value = Array.isArray(payload) ? payload : [];
  } catch (error) {
    console.error("Failed to load plans for analysis filters", error);
  }
};

const purchasesLoadedOnce = ref(false);
const purchaseWidgets = reactive(
  Object.fromEntries(
    PURCHASE_DIMENSION_OPTIONS.map((opt) => [
      opt.value,
      { purchaseRows: [], renewalRows: [], isLoading: false, errorMessage: "" },
    ])
  )
);

// Each dimension's response already contains both purchases[] and
// renewals[] (the backend computes both in one call) — cache both, so
// toggling the Type switch just re-renders from the already-fetched array
// instead of refetching.
const purchaseChartFor = (dimensionValue) => {
  const entry = purchaseWidgets[dimensionValue];
  const rows = purchaseType.value === "renewals" ? entry.renewalRows : entry.purchaseRows;
  const pivoted = pivotByDateAndBreakdown(rows, breakdownMetric.value === "count" ? "count" : "sumAmount");
  return capSeriesWithOthers(pivoted);
};

const loadPurchaseWidgets = async () => {
  syncUrl();
  isLoading.value = true;
  PURCHASE_DIMENSION_OPTIONS.forEach((opt) => {
    purchaseWidgets[opt.value].isLoading = true;
    purchaseWidgets[opt.value].errorMessage = "";
  });
  await Promise.all(
    PURCHASE_DIMENSION_OPTIONS.map(async (opt) => {
      const entry = purchaseWidgets[opt.value];
      try {
        const payload = await fetchPlansAnalysisReport({
          granularity: planFilters.granularity,
          dimension: opt.value,
          timezone: planFilters.timezone,
          ...toDateRangeParams(planFilters.dateFrom, planFilters.dateTo, planFilters.timezone),
          planId: planFilters.planIds,
          platform: planFilters.platforms,
          status: planFilters.statuses,
          gender: planFilters.genders,
          userPlanCountry: planFilters.userPlanCountries,
          addressCountry: planFilters.addressCountries,
          ageGroup: planFilters.ageGroups,
          minAge: planFilters.minAge > AGE_MIN ? planFilters.minAge : undefined,
          maxAge: planFilters.maxAge < AGE_MAX ? planFilters.maxAge : undefined,
          signInBy: planFilters.signInBys,
          verificationStatus: planFilters.verificationStatuses,
        });
        entry.purchaseRows = Array.isArray(payload?.purchases) ? payload.purchases : [];
        entry.renewalRows = Array.isArray(payload?.renewals) ? payload.renewals : [];
      } catch (error) {
        console.error(`Failed to load purchases analysis widget (${opt.value})`, error);
        entry.errorMessage =
          error?.payload?.message || error?.message || "Unable to load this widget.";
        entry.purchaseRows = [];
        entry.renewalRows = [];
      } finally {
        entry.isLoading = false;
      }
    })
  );
  isLoading.value = false;
  purchasesLoadedOnce.value = true;
};

const walletLoadedOnce = ref(false);
const walletWidgets = reactive(
  Object.fromEntries(
    WALLET_DIMENSION_OPTIONS.map((opt) => [opt.value, { rows: [], isLoading: false, errorMessage: "" }])
  )
);
const walletChartFor = (dimensionValue) => {
  const pivoted = pivotByDateAndBreakdown(
    walletWidgets[dimensionValue].rows,
    breakdownMetric.value === "count" ? "count" : "sumAmount"
  );
  return capSeriesWithOthers(pivoted);
};

const loadWalletWidgets = async () => {
  syncUrl();
  isLoading.value = true;
  WALLET_DIMENSION_OPTIONS.forEach((opt) => {
    walletWidgets[opt.value].isLoading = true;
    walletWidgets[opt.value].errorMessage = "";
  });
  await Promise.all(
    WALLET_DIMENSION_OPTIONS.map(async (opt) => {
      const entry = walletWidgets[opt.value];
      try {
        const payload = await fetchPlansAnalysisReport({
          granularity: planFilters.granularity,
          walletDimension: opt.value,
          includeSpent: includeSpent.value,
          timezone: planFilters.timezone,
          ...toDateRangeParams(planFilters.dateFrom, planFilters.dateTo, planFilters.timezone),
          platform: planFilters.platforms,
          status: planFilters.statuses,
          gender: planFilters.genders,
          addressCountry: planFilters.addressCountries,
          ageGroup: planFilters.ageGroups,
          minAge: planFilters.minAge > AGE_MIN ? planFilters.minAge : undefined,
          maxAge: planFilters.maxAge < AGE_MAX ? planFilters.maxAge : undefined,
          signInBy: planFilters.signInBys,
          verificationStatus: planFilters.verificationStatuses,
        });
        entry.rows = Array.isArray(payload?.walletTransactions) ? payload.walletTransactions : [];
      } catch (error) {
        console.error(`Failed to load wallet analysis widget (${opt.value})`, error);
        entry.errorMessage =
          error?.payload?.message || error?.message || "Unable to load this widget.";
        entry.rows = [];
      } finally {
        entry.isLoading = false;
      }
    })
  );
  isLoading.value = false;
  walletLoadedOnce.value = true;
};

// --- Complaints tab ------------------------------------------------------

const complaintFilters = reactive({
  granularity: queryStr("cGranularity", "day"),
  timezone: queryStr("cTimezone", DEFAULT_TIMEZONE),
  dateFrom: queryStr("cDateFrom", DEFAULT_DATE_FROM_STR),
  dateTo: queryStr("cDateTo", DEFAULT_DATE_TO_STR),
});

const complaintsLoadedOnce = ref(false);
const complaintWidgets = reactive(
  Object.fromEntries(
    COMPLAINT_DIMENSION_OPTIONS.map((opt) => [
      opt.value,
      { chart: { categories: [], series: [] }, isLoading: false, errorMessage: "" },
    ])
  )
);

// Same shape as the Users tab: the backend pivots (date bucket, dimension
// value) pairs into categories/series server-side, so widgets go straight
// to capSeriesWithOthers without a client-side pivot step.
const loadComplaintWidgets = async () => {
  syncUrl();
  isLoading.value = true;
  COMPLAINT_DIMENSION_OPTIONS.forEach((opt) => {
    complaintWidgets[opt.value].isLoading = true;
    complaintWidgets[opt.value].errorMessage = "";
  });
  await Promise.all(
    COMPLAINT_DIMENSION_OPTIONS.map(async (opt) => {
      const entry = complaintWidgets[opt.value];
      try {
        const payload = await fetchComplaintsAnalysisChart({
          dimension: opt.value,
          granularity: complaintFilters.granularity,
          timezone: complaintFilters.timezone,
          ...toDateRangeParams(complaintFilters.dateFrom, complaintFilters.dateTo, complaintFilters.timezone),
        });
        entry.chart = capSeriesWithOthers({
          categories: Array.isArray(payload?.categories) ? payload.categories : [],
          series: Array.isArray(payload?.series) ? payload.series : [],
        });
      } catch (error) {
        console.error(`Failed to load complaints analysis widget (${opt.value})`, error);
        entry.errorMessage =
          error?.payload?.message || error?.message || "Unable to load this widget.";
        entry.chart = { categories: [], series: [] };
      } finally {
        entry.isLoading = false;
      }
    })
  );
  isLoading.value = false;
  complaintsLoadedOnce.value = true;
};

// --- Messages tab ---------------------------------------------------------

const messageFilters = reactive({
  granularity: queryStr("mGranularity", "day"),
  timezone: queryStr("mTimezone", DEFAULT_TIMEZONE),
  dateFrom: queryStr("mDateFrom", DEFAULT_DATE_FROM_STR),
  dateTo: queryStr("mDateTo", DEFAULT_DATE_TO_STR),
});

const messagesLoadedOnce = ref(false);
const messageWidgets = reactive(
  Object.fromEntries(
    MESSAGE_DIMENSION_OPTIONS.map((opt) => [
      opt.value,
      { chart: { categories: [], series: [] }, isLoading: false, errorMessage: "" },
    ])
  )
);

const loadMessageWidgets = async () => {
  syncUrl();
  isLoading.value = true;
  MESSAGE_DIMENSION_OPTIONS.forEach((opt) => {
    messageWidgets[opt.value].isLoading = true;
    messageWidgets[opt.value].errorMessage = "";
  });
  await Promise.all(
    MESSAGE_DIMENSION_OPTIONS.map(async (opt) => {
      const entry = messageWidgets[opt.value];
      try {
        const payload = await fetchMessagesAnalysisChart({
          dimension: opt.value,
          granularity: messageFilters.granularity,
          timezone: messageFilters.timezone,
          ...toDateRangeParams(messageFilters.dateFrom, messageFilters.dateTo, messageFilters.timezone),
        });
        entry.chart = capSeriesWithOthers({
          categories: Array.isArray(payload?.categories) ? payload.categories : [],
          series: Array.isArray(payload?.series) ? payload.series : [],
        });
      } catch (error) {
        console.error(`Failed to load messages analysis widget (${opt.value})`, error);
        entry.errorMessage =
          error?.payload?.message || error?.message || "Unable to load this widget.";
        entry.chart = { categories: [], series: [] };
      } finally {
        entry.isLoading = false;
      }
    })
  );
  isLoading.value = false;
  messagesLoadedOnce.value = true;
};

// --- Relations tab ---------------------------------------------------------

const relationFilters = reactive({
  granularity: queryStr("rGranularity", "day"),
  timezone: queryStr("rTimezone", DEFAULT_TIMEZONE),
  dateFrom: queryStr("rDateFrom", DEFAULT_DATE_FROM_STR),
  dateTo: queryStr("rDateTo", DEFAULT_DATE_TO_STR),
});

const relationsLoadedOnce = ref(false);
const relationWidgets = reactive(
  Object.fromEntries(
    RELATION_DIMENSION_OPTIONS.map((opt) => [
      opt.value,
      { chart: { categories: [], series: [] }, isLoading: false, errorMessage: "" },
    ])
  )
);

const loadRelationWidgets = async () => {
  syncUrl();
  isLoading.value = true;
  RELATION_DIMENSION_OPTIONS.forEach((opt) => {
    relationWidgets[opt.value].isLoading = true;
    relationWidgets[opt.value].errorMessage = "";
  });
  await Promise.all(
    RELATION_DIMENSION_OPTIONS.map(async (opt) => {
      const entry = relationWidgets[opt.value];
      try {
        const payload = await fetchRelationsAnalysisChart({
          dimension: opt.value,
          granularity: relationFilters.granularity,
          timezone: relationFilters.timezone,
          ...toDateRangeParams(relationFilters.dateFrom, relationFilters.dateTo, relationFilters.timezone),
        });
        entry.chart = capSeriesWithOthers({
          categories: Array.isArray(payload?.categories) ? payload.categories : [],
          series: Array.isArray(payload?.series) ? payload.series : [],
        });
      } catch (error) {
        console.error(`Failed to load relations analysis widget (${opt.value})`, error);
        entry.errorMessage =
          error?.payload?.message || error?.message || "Unable to load this widget.";
        entry.chart = { categories: [], series: [] };
      } finally {
        entry.isLoading = false;
      }
    })
  );
  isLoading.value = false;
  relationsLoadedOnce.value = true;
};

// --- Shared --------------------------------------------------------------

// Mirrors the active tab + its filters into the URL so a refresh lands back
// on the same view. Only the active tab's group is written — the other
// group's keys are simply omitted rather than cleared, so jumping back to
// that tab later (without ever touching its filters) still restores
// whatever was last set on it.
const buildQueryFromState = () => {
  const query = {};
  if (activeTabKey.value !== TABS[0].key) query.tab = activeTabKey.value;
  if (isUserTab.value) {
    if (userFilters.dateField !== "createdAt") query.uDateField = userFilters.dateField;
    if (userFilters.granularity !== "day") query.uGranularity = userFilters.granularity;
    if (userFilters.timezone !== DEFAULT_TIMEZONE) query.uTimezone = userFilters.timezone;
    if (userFilters.dateFrom !== DEFAULT_DATE_FROM_STR) query.uDateFrom = userFilters.dateFrom;
    if (userFilters.dateTo !== DEFAULT_DATE_TO_STR) query.uDateTo = userFilters.dateTo;
    if (userFilters.countryCodes.length) query.uCountryCodes = userFilters.countryCodes.join(",");
    if (userFilters.platform) query.uPlatform = userFilters.platform;
    if (userFilters.minAge > AGE_MIN) query.uMinAge = String(userFilters.minAge);
    if (userFilters.maxAge < AGE_MAX) query.uMaxAge = String(userFilters.maxAge);
    if (userFilters.statuses.length) query.uStatuses = userFilters.statuses.join(",");
    if (userFilters.genders.length) query.uGenders = userFilters.genders.join(",");
    if (userFilters.ageGroups.length) query.uAgeGroups = userFilters.ageGroups.join(",");
    if (userFilters.activePlanIds.length) query.uActivePlanIds = userFilters.activePlanIds.join(",");
  } else if (isComplaintTab.value) {
    if (complaintFilters.granularity !== "day") query.cGranularity = complaintFilters.granularity;
    if (complaintFilters.timezone !== DEFAULT_TIMEZONE) query.cTimezone = complaintFilters.timezone;
    if (complaintFilters.dateFrom !== DEFAULT_DATE_FROM_STR) query.cDateFrom = complaintFilters.dateFrom;
    if (complaintFilters.dateTo !== DEFAULT_DATE_TO_STR) query.cDateTo = complaintFilters.dateTo;
  } else if (isMessageTab.value) {
    if (messageFilters.granularity !== "day") query.mGranularity = messageFilters.granularity;
    if (messageFilters.timezone !== DEFAULT_TIMEZONE) query.mTimezone = messageFilters.timezone;
    if (messageFilters.dateFrom !== DEFAULT_DATE_FROM_STR) query.mDateFrom = messageFilters.dateFrom;
    if (messageFilters.dateTo !== DEFAULT_DATE_TO_STR) query.mDateTo = messageFilters.dateTo;
  } else if (isRelationTab.value) {
    if (relationFilters.granularity !== "day") query.rGranularity = relationFilters.granularity;
    if (relationFilters.timezone !== DEFAULT_TIMEZONE) query.rTimezone = relationFilters.timezone;
    if (relationFilters.dateFrom !== DEFAULT_DATE_FROM_STR) query.rDateFrom = relationFilters.dateFrom;
    if (relationFilters.dateTo !== DEFAULT_DATE_TO_STR) query.rDateTo = relationFilters.dateTo;
  } else {
    if (planFilters.granularity !== "day") query.pGranularity = planFilters.granularity;
    if (planFilters.timezone !== DEFAULT_TIMEZONE) query.pTimezone = planFilters.timezone;
    if (includeSpent.value) query.pIncludeSpent = "true";
    if (planFilters.dateFrom !== DEFAULT_DATE_FROM_STR) query.pDateFrom = planFilters.dateFrom;
    if (planFilters.dateTo !== DEFAULT_DATE_TO_STR) query.pDateTo = planFilters.dateTo;
    if (planFilters.addressCountries.length) query.pAddressCountries = planFilters.addressCountries.join(",");
    if (planFilters.userPlanCountries.length) {
      query.pUserPlanCountries = planFilters.userPlanCountries.join(",");
    }
    if (planFilters.platforms.length) query.pPlatforms = planFilters.platforms.join(",");
    if (planFilters.minAge > AGE_MIN) query.pMinAge = String(planFilters.minAge);
    if (planFilters.maxAge < AGE_MAX) query.pMaxAge = String(planFilters.maxAge);
    if (planFilters.statuses.length) query.pStatuses = planFilters.statuses.join(",");
    if (planFilters.genders.length) query.pGenders = planFilters.genders.join(",");
    if (planFilters.ageGroups.length) query.pAgeGroups = planFilters.ageGroups.join(",");
    if (planFilters.signInBys.length) query.pSignInBys = planFilters.signInBys.join(",");
    if (planFilters.planIds.length) query.pPlanIds = planFilters.planIds.join(",");
    if (planFilters.verificationStatuses.length) {
      query.pVerificationStatuses = planFilters.verificationStatuses.join(",");
    }
    if (breakdownMetric.value !== "amount") query.pBreakdownMetric = breakdownMetric.value;
    if (purchaseType.value !== "purchases") query.pPurchaseType = purchaseType.value;
  }
  return query;
};

const syncUrl = () => {
  router.replace({ query: buildQueryFromState() });
};

const onApply = () => {
  if (isUserTab.value) loadUsersWidgets();
  else if (isPurchasesTab.value) loadPurchaseWidgets();
  else if (isWalletTab.value) loadWalletWidgets();
  else if (isComplaintTab.value) loadComplaintWidgets();
  else if (isMessageTab.value) loadMessageWidgets();
  else loadRelationWidgets();
};

watch(activeTabKey, () => {
  syncUrl();
  if (isUserTab.value && !usersLoadedOnce.value) loadUsersWidgets();
  else if (isPurchasesTab.value && !purchasesLoadedOnce.value) loadPurchaseWidgets();
  else if (isWalletTab.value && !walletLoadedOnce.value) loadWalletWidgets();
  else if (isComplaintTab.value && !complaintsLoadedOnce.value) loadComplaintWidgets();
  else if (isMessageTab.value && !messagesLoadedOnce.value) loadMessageWidgets();
  else if (isRelationTab.value && !relationsLoadedOnce.value) loadRelationWidgets();
});

// Display-only toggles that don't need a refetch (both purchases and
// renewals rows are already fetched together, and the metric is computed
// client-side from already-fetched rows) still belong in the URL.
watch(purchaseType, syncUrl);
watch(breakdownMetric, syncUrl);

// Unlike the toggles above, includeSpent is a real backend filter, so it
// does need a refetch.
watch(includeSpent, () => {
  syncUrl();
  if (walletLoadedOnce.value) loadWalletWidgets();
});

onMounted(() => {
  loadPlans();
  if (isUserTab.value) loadUsersWidgets();
  else if (isPurchasesTab.value) loadPurchaseWidgets();
  else if (isWalletTab.value) loadWalletWidgets();
  else if (isComplaintTab.value) loadComplaintWidgets();
  else if (isMessageTab.value) loadMessageWidgets();
  else loadRelationWidgets();
});
</script>

<template>
  <div class="py-4 container-fluid">
    <ul class="nav nav-pills flex-wrap gap-2 mb-4">
      <li v-for="tab in TABS" :key="tab.key" class="nav-item">
        <button type="button" class="nav-link mb-0" :class="{ active: activeTabKey === tab.key }"
          @click="activeTabKey = tab.key">
          {{ tab.label }}
        </button>
      </li>
    </ul>

    <div class="card mb-4">
      <div class="card-body">
        <UserFilters v-if="isUserTab" v-model:filters="userFilters" :plans="plans" />
        <div v-else-if="isComplaintTab" class="filters-bar">
          <DateGranularityFilter v-model:date-from="complaintFilters.dateFrom"
            v-model:date-to="complaintFilters.dateTo" v-model:granularity="complaintFilters.granularity"
            v-model:timezone="complaintFilters.timezone" />
        </div>
        <div v-else-if="isMessageTab" class="filters-bar">
          <DateGranularityFilter v-model:date-from="messageFilters.dateFrom"
            v-model:date-to="messageFilters.dateTo" v-model:granularity="messageFilters.granularity"
            v-model:timezone="messageFilters.timezone" />
        </div>
        <div v-else-if="isRelationTab" class="filters-bar">
          <DateGranularityFilter v-model:date-from="relationFilters.dateFrom"
            v-model:date-to="relationFilters.dateTo" v-model:granularity="relationFilters.granularity"
            v-model:timezone="relationFilters.timezone" />
        </div>
        <PlanFilters v-else v-model:filters="planFilters" :plans="plans" :is-wallet-tab="isWalletTab"
          :is-purchases-tab="isPurchasesTab"
          v-model:purchase-type="purchaseType" v-model:include-spent="includeSpent"
          v-model:breakdown-metric="breakdownMetric" />

        <div class="mt-3">
          <button class="btn btn-sm btn-success mb-0" type="button" :disabled="isLoading" @click="onApply">
            <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
            <span v-else>Apply</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="isWalletTab && canViewUtopiaBalance" class="card mb-4">
      <div class="card-body">
        <div class="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <h6 class="mb-1">Utopia balance</h6>
            <p class="text-xs text-secondary mb-0">
              Current UUSD balance in the Utopia merchant account (live, not cached).
            </p>
          </div>
          <button class="btn btn-sm btn-success mb-0" type="button" :disabled="utopiaBalanceLoading"
            @click="checkUtopiaBalance">
            <span v-if="utopiaBalanceLoading" class="spinner-border spinner-border-sm"></span>
            <span v-else>Check balance</span>
          </button>
        </div>
        <div v-if="utopiaBalanceError" class="text-xs text-danger mt-2">{{ utopiaBalanceError }}</div>
        <div v-else-if="utopiaBalance !== null" class="mt-2">
          <p class="text-lg fw-bold mb-0">{{ utopiaBalance.toFixed(2) }} UUSD</p>
          <p class="text-xs text-secondary mb-0">Checked at {{ formatTimestamp(utopiaBalanceCheckedAt) }}</p>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <template v-if="isUserTab">
        <div v-for="opt in Y_DIMENSION_OPTIONS" :key="opt.value" class="col-12 col-md-6 col-xl-4">
          <DimensionWidget :title="opt.label" :categories="userWidgets[opt.value].chart.categories"
            :series="userWidgets[opt.value].chart.series" :is-loading="userWidgets[opt.value].isLoading"
            :error-message="userWidgets[opt.value].errorMessage" />
        </div>
      </template>
      <template v-else-if="isPurchasesTab">
        <div v-for="opt in PURCHASE_DIMENSION_OPTIONS" :key="opt.value" class="col-12 col-md-6 col-xl-4">
          <DimensionWidget :title="opt.label" v-bind="purchaseChartFor(opt.value)"
            :is-money="breakdownMetric === 'amount'" :is-loading="purchaseWidgets[opt.value].isLoading"
            :error-message="purchaseWidgets[opt.value].errorMessage" />
        </div>
      </template>
      <template v-else-if="isWalletTab">
        <div v-for="opt in WALLET_DIMENSION_OPTIONS" :key="opt.value" class="col-12 col-md-6 col-xl-4">
          <DimensionWidget :title="opt.label" v-bind="walletChartFor(opt.value)"
            :is-money="breakdownMetric === 'amount'" :is-loading="walletWidgets[opt.value].isLoading"
            :error-message="walletWidgets[opt.value].errorMessage" />
        </div>
      </template>
      <template v-else-if="isComplaintTab">
        <div v-for="opt in COMPLAINT_DIMENSION_OPTIONS" :key="opt.value" class="col-12 col-md-6 col-xl-4">
          <DimensionWidget :title="opt.label" :categories="complaintWidgets[opt.value].chart.categories"
            :series="complaintWidgets[opt.value].chart.series" :is-loading="complaintWidgets[opt.value].isLoading"
            :error-message="complaintWidgets[opt.value].errorMessage" />
        </div>
      </template>
      <template v-else-if="isMessageTab">
        <div v-for="opt in MESSAGE_DIMENSION_OPTIONS" :key="opt.value" class="col-12 col-md-6 col-xl-4">
          <DimensionWidget :title="opt.label" :categories="messageWidgets[opt.value].chart.categories"
            :series="messageWidgets[opt.value].chart.series" :is-loading="messageWidgets[opt.value].isLoading"
            :error-message="messageWidgets[opt.value].errorMessage" />
        </div>
      </template>
      <template v-else-if="isRelationTab">
        <div v-for="opt in RELATION_DIMENSION_OPTIONS" :key="opt.value" class="col-12 col-md-6 col-xl-4">
          <DimensionWidget :title="opt.label" :categories="relationWidgets[opt.value].chart.categories"
            :series="relationWidgets[opt.value].chart.series" :is-loading="relationWidgets[opt.value].isLoading"
            :error-message="relationWidgets[opt.value].errorMessage" />
        </div>
      </template>
    </div>
  </div>
</template>

<style>
.filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  align-items: flex-end;
}

.filter-block {
  min-width: 220px;
}

.filter-block-sm {
  min-width: 140px;
}

.filter-toggle-group {
  flex-basis: 100%;
}
</style>
